import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Webhook, Database, Copy, CheckCircle2, RefreshCw, Send, ExternalLink, AlertCircle, Code, KeyRound, Edit2, Save, X, LayoutDashboard, Target, ArrowRight, Flame, TrendingUp, CircleDot, Settings, ShieldCheck, Clock, RotateCcw, Download, FileSpreadsheet } from "lucide-react";
import CSVUploadCard from "../components/CSVUploadCard";
import FormsPerformanceCard from "../components/FormsPerformanceCard";
import DigitalLeadsCSVCard from "../components/DigitalLeadsCSVCard";
import SalesUploadCard from "../components/SalesUploadCard";
import SeminuevosUploadCard from "../components/SeminuevosUploadCard";
import InventoryUploadCard from "../components/InventoryUploadCard";
import TargetsUploadCard from "../components/TargetsUploadCard";
import ArchiveCard from "../components/ArchiveCard";
import BackupDownloadCard from "../components/BackupDownloadCard";
import BackupRestoreCard from "../components/BackupRestoreCard";
import DashboardVisibilityCard from "../components/DashboardVisibilityCard";
import WeeklyDigestCard from "../components/WeeklyDigestCard";
import { ProjectionsUploadCard } from "../components/ProjectionKpiCard";
import { TradeInUploadCard, TRADE_IN_ENABLED } from "../components/TradeInCard";
import ResumenTab from "../components/ResumenTab";
import { ZonasHotUploadCard } from "../components/ZonasHotUploadCard";
import ForecastRequirementsCard from "../components/ForecastRequirementsCard";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmtDate = (s) => s ? new Date(s).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" }) : "—";

const TABS = [
  { id: "resumen",    label: "Resumen",            icon: <CircleDot size={14}/>,        accent: "#7c3aed" },
  { id: "crm",        label: "CRM Overview",       icon: <LayoutDashboard size={14}/>,  accent: "#111827" },
  { id: "leads",      label: "Lead Performance",   icon: <Target size={14}/>,           accent: "#f4c400" },
  { id: "predictivo", label: "Forecast",           icon: <TrendingUp size={14}/>,       accent: "#3b82f6" },
  { id: "zonashot",   label: "Zonas Hot SDM",      icon: <Flame size={14}/>,            accent: "#ea580c" },
  { id: "sistema",    label: "Sistema · Backup",   icon: <Database size={14}/>,         accent: "#16a34a" },
  { id: "ajustes",    label: "Ajustes Admin",      icon: <Settings size={14}/>,         accent: "#0ea5e9" },
];

export default function AdminIntegrationsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("integrations_tab") || "resumen");

  useEffect(() => { localStorage.setItem("integrations_tab", tab); }, [tab]);

  const load = () => {
    setLoading(true);
    api.get("/admin/integrations").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const handler = () => load();
    const tabHandler = (e) => { if (e.detail && TABS.find((t) => t.id === e.detail)) setTab(e.detail); };
    window.addEventListener("integrations:refresh", handler);
    window.addEventListener("integrations:set-tab", tabHandler);
    return () => {
      window.removeEventListener("integrations:refresh", handler);
      window.removeEventListener("integrations:set-tab", tabHandler);
    };
  }, []);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/integrations/zapier/test");
      setTestResult({ ok: true, msg: r.data.message || "Lead de prueba creado correctamente", id: r.data.id });
      setTimeout(load, 600);
    } catch (e) {
      setTestResult({ ok: false, msg: e.response?.data?.detail || "Error al ejecutar la prueba" });
    } finally {
      setTesting(false);
    }
  };

  if (loading || !data) {
    return <div className="min-h-[60vh] grid place-items-center"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}/></div>;
  }

  return (
    <div data-testid="admin-integrations-page" className="pb-12">
      <header className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold" style={{ letterSpacing: "-0.02em" }}>Integraciones</h1>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Gestiona las fuentes de datos por categoría · cada pestaña agrupa las integraciones relacionadas.
          </p>
        </div>
        <button className="btn flex items-center gap-2" onClick={load} data-testid="admin-refresh">
          <RefreshCw size={14}/> Refrescar
        </button>
      </header>

      <MaintenanceCard/>
      <MigrateBusinessUnitsCard/>

      {/* Sticky pill-style tabs */}
      <div className="sticky z-30 mb-6 -mx-6 px-6 py-3 backdrop-blur-md"
           style={{ top: 0, background: "rgba(250,250,250,0.92)", borderBottom: "1px solid #e5e7eb" }}
           data-testid="integrations-tabs">
        <div className="overflow-x-auto hide-scrollbar">
          <div className="inline-flex bg-gray-100/70 p-1 rounded-xl gap-1" role="tablist" aria-label="Categorías de integraciones">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  aria-controls={`tab-panel-${t.id}`}
                  id={`tab-trigger-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="px-4 py-2 rounded-lg text-sm font-extrabold transition-all flex items-center gap-2 whitespace-nowrap"
                  style={{
                    background: active ? "#ffffff" : "transparent",
                    color: active ? "#111827" : "#6b7280",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                  data-testid={`tab-${t.id}`}
                >
                  <span style={{ color: active ? t.accent : "#9ca3af" }}>{t.icon}</span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab contents */}
      {tab === "resumen" && (
        <ResumenTab data={data} onJump={(target) => setTab(target)}/>
      )}

      {tab === "crm" && (
        <DashboardSection
          icon={<LayoutDashboard size={20}/>}
          accent="#111827"
          title="CRM Overview"
          subtitle="Alimentado por export CSV manual desde PowerBI / Dynamics 365."
          linkTo="/crm-overview"
          linkLabel="Ir al dashboard"
          testid="section-crm"
        >
          <PowerBICard data={data.powerbi_csv} onUploaded={load}/>
          <ProjectionsUploadCard/>
          <CrmXlsxUploadCard
            title="Oportunidades (Test Drive)"
            subtitle="XLSX de Dynamics 365 · columna «¿Tomó Test Drive CC?». Alimenta la etapa Test Drive del Sales Funnel."
            endpoint="/sales-performance/uploads/oportunidades"
            statusKey="oportunidades"
            testid="crm-oportunidades-upload"
          />
          <CrmXlsxUploadCard
            title="Cotizaciones"
            subtitle="XLSX de Dynamics 365 · Alimenta la etapa Cotización del Sales Funnel."
            endpoint="/sales-performance/uploads/cotizaciones"
            statusKey="cotizaciones"
            testid="crm-cotizaciones-upload"
          />
          {TRADE_IN_ENABLED && <TradeInUploadCard/>}
          <MasterTemplateCard/>
        </DashboardSection>
      )}

      {tab === "leads" && (
        <DashboardSection
          icon={<Target size={20}/>}
          accent="#f4c400"
          title="Lead Performance"
          subtitle="Alimentado por Zapier webhook en tiempo real. Canales digitales: Meta Ads, Google Ads, TikTok, Email, etc."
          linkTo="/leads-performance"
          linkLabel="Ir al dashboard"
          testid="section-leads"
        >
          <ZapierCard data={data.zapier} testing={testing} testResult={testResult} onTest={runTest}/>
          <DigitalLeadsCSVCard onUploaded={load}/>
          <FormsPerformanceCard/>
        </DashboardSection>
      )}

      {tab === "predictivo" && (
        <DashboardSection
          icon={<TrendingUp size={20}/>}
          accent="#3b82f6"
          title="Forecast"
          subtitle="Calcula la conversión Leads → Ventas y proyecta el forecast a 3 meses. Combina datos del CRM (leads) + Facturación (ventas) para funcionar correctamente."
          linkTo="/predictive"
          linkLabel="Ir al dashboard"
          testid="section-predictive"
        >
          <ForecastRequirementsCard data={data}/>
          <SalesUploadCard/>
          <SeminuevosUploadCard/>
          <InventoryUploadCard/>
          <TargetsUploadCard onUploaded={load}/>
        </DashboardSection>
      )}

      {tab === "zonashot" && (
        <DashboardSection
          icon={<Flame size={20}/>}
          accent="#ea580c"
          title="Zonas Hot SDM"
          subtitle="Ventas de las Zonas Hot por sucursal, showroom y marca. Sube el Excel «Ventas Zona Hot»."
          linkTo="/zonas-hot"
          linkLabel="Ir al dashboard"
          testid="section-zonas-hot"
        >
          <ZonasHotUploadCard/>
        </DashboardSection>
      )}

      {tab === "sistema" && (
        <>
          <DashboardSection
            icon={<ShieldCheck size={20}/>}
            accent="#0ea5e9"
            title="Backup Descargable"
            subtitle="Exporta un ZIP con toda la data operativa. Guárdalo cifrado antes de operaciones destructivas o como respaldo periódico local."
            linkTo={null}
            linkLabel={null}
            testid="section-backup-download"
          >
            <BackupDownloadCard/>
          </DashboardSection>
          <DashboardSection
            icon={<RotateCcw size={20}/>}
            accent="#dc2626"
            title="Restaurar desde Backup"
            subtitle="Re-inyecta un ZIP-backup para revertir un wipeout accidental (ej. un REPLACE parcial). Operación destructiva: requiere confirmación explícita."
            linkTo={null}
            linkLabel={null}
            testid="section-backup-restore"
          >
            <BackupRestoreCard/>
          </DashboardSection>
          <DashboardSection
            icon={<Database size={20}/>}
            accent="#16a34a"
            title="Sistema · Backup Permanente"
            subtitle="Archive append-only en MongoDB. Protege contra los límites de retención de Zapier (60 días) y Meta (90 días)."
            linkTo={null}
            linkLabel={null}
            testid="section-archive"
          >
            <ArchiveCard/>
          </DashboardSection>
        </>
      )}

      {tab === "ajustes" && (
        <DashboardSection
          icon={<Settings size={20}/>}
          accent="#0ea5e9"
          title="Ajustes de administrador"
          subtitle="Controles globales que afectan a todos los usuarios — visibilidad de dashboards, accesos."
          linkTo={null}
          linkLabel={null}
          testid="section-admin"
        >
          <WeeklyDigestCard/>
          <DashboardVisibilityCard/>
        </DashboardSection>
      )}
    </div>
  );
}

function DashboardSection({ icon, accent, title, subtitle, linkTo, linkLabel, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-4 mb-4 pb-3" style={{ borderBottom: `2px solid ${accent}22` }}>
        <div className="kpi-icon-clean" style={{ width: 44, height: 44, color: accent, boxShadow: `inset 0 0 0 1px ${accent}33` }}>{icon}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-extrabold" style={{ letterSpacing: "-0.02em" }}>{title}</h2>
            <span className="pill" style={{ background: `${accent}14`, color: accent, fontSize: 10, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}>Dashboard</span>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">{subtitle}</p>
        </div>
        {linkTo && (
          <Link to={linkTo} className="btn flex items-center gap-2 no-underline" data-testid={`${testid}-link`}>
            {linkLabel} <ArrowRight size={14}/>
          </Link>
        )}
      </div>
      <div className="space-y-4 pl-2">
        {children}
      </div>
    </section>
  );
}

function ZapierCard({ data, testing, testResult, onTest }) {
  const [editing, setEditing] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [activeTab, setActiveTab] = useState("zapier");

  const startEdit = () => { setNewToken(data.token_full || ""); setEditing(true); setSaveMsg(null); };
  const cancel = () => { setEditing(false); setSaveMsg(null); };

  const save = async (auto = false) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const body = auto ? { token: "" } : { token: newToken };
      const r = await api.patch("/admin/integrations/zapier/token", body);
      setSaveMsg({ ok: true, msg: `Token actualizado · ${r.data.preview}` });
      setEditing(false);
      window.dispatchEvent(new CustomEvent("integrations:refresh"));
    } catch (e) {
      setSaveMsg({ ok: false, msg: e.response?.data?.detail || "Error al actualizar el token" });
    } finally {
      setSaving(false);
    }
  };

  const tabHeader = activeTab === "zapier" ? data.header_name : (data.alt_header_name || "X-Webhook-Token");

  return (
    <section className="surface p-6" data-testid="card-zapier">
      <div className="flex items-start gap-4 mb-5">
        <div className="kpi-icon-clean" style={{ width: 48, height: 48, color: "#ff4a00", background: "#fff5ef", boxShadow: "inset 0 0 0 1px #ff4a001f" }}>
          <Webhook size={22}/>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-extrabold">Webhook de Leads Digitales</h2>
            <StatusBadge ok={data.configured} okText="Activo" badText="No configurado"/>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Recibe leads en tiempo real desde <strong>Zapier</strong>, <strong>WPForms Webhooks</strong>, o cualquier fuente HTTP. El mismo endpoint y token sirven para ambos.
          </p>
        </div>
      </div>

      <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <Stat label="Leads recibidos" value={fmt(data.leads_received)}/>
        <Stat label="Último lead" value={fmtDate(data.last_lead_at)}/>
        <Stat label="Método" value={`${data.method}`}/>
      </div>

      <div className="space-y-3 mb-5">
        <CopyField label="URL del webhook" value={data.webhook_url} testid="zap-url"/>

        {/* Token field with edit/regenerate */}
        <div>
          <div className="label-up mb-1 flex items-center gap-2">
            <KeyRound size={11}/> Header HTTP de autenticación ({tabHeader})
            {!editing && (
              <span className="ml-auto flex gap-2">
                <button className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1" onClick={startEdit} data-testid="zap-token-edit">
                  <Edit2 size={11}/> Editar
                </button>
                <button className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1" onClick={() => save(true)} disabled={saving} data-testid="zap-token-regenerate">
                  <RefreshCw size={11} className={saving ? "animate-spin" : ""}/> Regenerar
                </button>
              </span>
            )}
          </div>
          {!editing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={data.token_full || data.token_preview}
                readOnly
                className="text-input flex-1"
                style={{ fontFamily: "'Outfit', monospace", fontSize: 13 }}
                data-testid="zap-token"
              />
              <CopyButton value={data.token_full}/>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                placeholder="Nuevo token (mín 12 caracteres)"
                className="text-input flex-1"
                style={{ fontFamily: "'Outfit', monospace", fontSize: 13 }}
                data-testid="zap-token-input"
                autoFocus
              />
              <button className="btn primary flex items-center gap-1" onClick={() => save(false)} disabled={saving || newToken.length < 12} data-testid="zap-token-save">
                {saving ? <span className="spinner"/> : <Save size={14}/>} Guardar
              </button>
              <button className="btn flex items-center gap-1" onClick={cancel}>
                <X size={14}/>
              </button>
            </div>
          )}
          {saveMsg && (
            <div className={`mt-2 pill text-xs ${saveMsg.ok ? "good" : "bad"}`}>
              {saveMsg.ok ? <CheckCircle2 size={12}/> : <AlertCircle size={12}/>} {saveMsg.msg}
            </div>
          )}
          <div className="text-xs text-gray-400 mt-1">
            Cambiar el token invalida tanto Zaps como Webhooks WP existentes — actualízalos después de guardar.
          </div>
        </div>
      </div>

      <PayloadExamples payloads={data.sample_payloads} singlePayload={data.sample_payload}/>

      <details className="surface mb-4" style={{ background: "#fafbfc", padding: 0 }}>
        <summary className="cursor-pointer flex items-center gap-2 font-display font-extrabold p-4">
          <ExternalLink size={16}/> Guía paso a paso — Conectar Zapier o WPForms
        </summary>
        <div className="px-4 pb-4">

      {/* Tabs */}
      <div className="lp2-tabs mb-3" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === "zapier"}
          className={`lp2-tab ${activeTab === "zapier" ? "is-active" : ""}`}
          onClick={() => setActiveTab("zapier")}
          data-testid="tab-zapier"
        >
          <ExternalLink size={13}/> Conectar con Zapier
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "wpforms"}
          className={`lp2-tab ${activeTab === "wpforms" ? "is-active" : ""}`}
          onClick={() => setActiveTab("wpforms")}
          data-testid="tab-wpforms"
        >
          <ExternalLink size={13}/> Conectar con WPForms
        </button>
      </div>

      {activeTab === "zapier" && (
        <div className="surface p-4 mb-4" style={{ background: "#fafbfc" }} data-testid="tab-zapier-content">
          <ol className="ml-4 list-decimal text-sm font-semibold text-gray-700 space-y-2">
            <li>En Zapier, crea un nuevo <strong>Zap</strong>. Como Trigger elige <strong>&ldquo;New Form Entry in WPForms&rdquo;</strong> (o Meta Lead Ads, Google Lead Form, etc.).</li>
            <li>Como Action elige <strong>Webhooks by Zapier → POST</strong>.</li>
            <li>En <strong>URL</strong> pega el endpoint del webhook (arriba).</li>
            <li>En <strong>Headers</strong> añade <code className="px-1 rounded bg-gray-100">{data.header_name}</code> con el valor del token.</li>
            <li>En <strong>Data</strong> mapea los campos: <code>email</code>, <code>phone</code>, <code>first_name</code>, <code>last_name</code>, <code>brand</code>, <code>model</code>, <code>branch</code>, y los UTMs (<code>utm_source</code>, <code>utm_campaign</code>, etc.) tomados de los campos ocultos del form.</li>
            <li><strong>Tip</strong>: usa el nombre del formulario como <code>campaign</code> si no tienes un campo dedicado, o pásalo en <code>wp_form_name</code>.</li>
            <li>Activa el Zap. Los leads aparecerán en <em>Lead Performance</em> en segundos.</li>
          </ol>
        </div>
      )}

      {activeTab === "wpforms" && (
        <div className="surface p-4 mb-4" style={{ background: "#fafbfc" }} data-testid="tab-wpforms-content">
          <p className="text-sm font-semibold text-gray-700 mb-3">
            Requiere <strong>WPForms Pro</strong> + <strong>Webhooks Addon</strong> activado.
          </p>
          <ol className="ml-4 list-decimal text-sm font-semibold text-gray-700 space-y-3">
            <li>En el editor del formulario WP, ve a <strong>Settings → Webhooks → Add New Webhook</strong>. Activa &ldquo;Enable Webhooks&rdquo;.</li>
            <li>Configura:
              <ul className="ml-5 list-disc mt-1">
                <li><strong>Request URL</strong>: el endpoint del webhook (arriba)</li>
                <li><strong>Request Method</strong>: POST · <strong>Request Format</strong>: JSON</li>
                <li><strong>Secret</strong>: DEJAR VACÍO (ese campo es para HMAC, no usamos eso)</li>
              </ul>
            </li>
            <li>
              <strong>Request Headers</strong> (1 fila):
              <table className="w-full mt-2 text-xs border border-gray-200 rounded">
                <thead className="bg-gray-100">
                  <tr><th className="text-left p-2">Parameter key</th><th className="text-left p-2">Valor</th></tr>
                </thead>
                <tbody>
                  <tr><td className="p-2 font-mono"><code>X-Webhook-Token</code></td><td className="p-2 font-mono break-all">{data.token_full || data.token_preview}</td></tr>
                </tbody>
              </table>
              <div className="text-xs text-gray-500 mt-2">
                💡 Si tu versión solo permite seleccionar campos del form (no valores fijos):
                crea un <strong>campo oculto</strong> en el form llamado <code>auth_token</code> con
                <strong> Default Value</strong> = el token, y selecciónalo aquí.
              </div>
            </li>
            <li>
              <strong>Request Body</strong> (añade 14 filas):
              <table className="w-full mt-2 text-xs border border-gray-200 rounded">
                <thead className="bg-gray-100">
                  <tr><th className="text-left p-2">Parameter key</th><th className="text-left p-2">Select Field</th></tr>
                </thead>
                <tbody className="font-mono">
                  <tr><td className="p-1.5">first_name</td><td className="p-1.5 text-gray-600">→ tu campo Nombre</td></tr>
                  <tr><td className="p-1.5">last_name</td><td className="p-1.5 text-gray-600">→ tu campo Apellido</td></tr>
                  <tr><td className="p-1.5">email</td><td className="p-1.5 text-gray-600">→ tu campo Email</td></tr>
                  <tr><td className="p-1.5">phone</td><td className="p-1.5 text-gray-600">→ tu campo Teléfono</td></tr>
                  <tr><td className="p-1.5">brand</td><td className="p-1.5 text-gray-600">→ tu campo Marca de interés</td></tr>
                  <tr><td className="p-1.5">model</td><td className="p-1.5 text-gray-600">→ tu campo Modelo</td></tr>
                  <tr><td className="p-1.5">branch</td><td className="p-1.5 text-gray-600">→ tu campo Sucursal</td></tr>
                  <tr className="bg-yellow-50"><td className="p-1.5">utm_source</td><td className="p-1.5 text-gray-600">→ hidden field utm_source</td></tr>
                  <tr className="bg-yellow-50"><td className="p-1.5">utm_medium</td><td className="p-1.5 text-gray-600">→ hidden field utm_medium</td></tr>
                  <tr className="bg-yellow-50"><td className="p-1.5">utm_campaign</td><td className="p-1.5 text-gray-600">→ hidden field utm_campaign (campaign ID)</td></tr>
                  <tr className="bg-yellow-50"><td className="p-1.5">utm_content</td><td className="p-1.5 text-gray-600">→ hidden field utm_content</td></tr>
                  <tr className="bg-yellow-50"><td className="p-1.5">utm_term</td><td className="p-1.5 text-gray-600">→ hidden field utm_term</td></tr>
                  <tr className="bg-blue-50"><td className="p-1.5">wp_form_id</td><td className="p-1.5 text-gray-600">→ Smart Tag · Entry → Form ID</td></tr>
                  <tr className="bg-blue-50"><td className="p-1.5">wp_form_name</td><td className="p-1.5 text-gray-600">→ Smart Tag · Entry → Form Name</td></tr>
                </tbody>
              </table>
            </li>
            <li>
              <strong>Hidden Fields para UTM</strong> (si aún no los tienes):
              <ul className="ml-5 list-disc mt-1">
                <li>Añade 5 hidden fields al form: <code>utm_source</code>, <code>utm_medium</code>, <code>utm_campaign</code>, <code>utm_content</code>, <code>utm_term</code></li>
                <li>En el <strong>Default Value</strong> de cada uno usa el smart tag <code className="bg-gray-100 px-1 rounded">{`{query_var key="utm_source"}`}</code> para capturar el query string de la URL automáticamente.</li>
              </ul>
            </li>
            <li>Guarda. Prueba enviando el form con una URL tipo <code>tu-sitio.com/formulario?utm_source=facebook&utm_campaign=suv_lanzamiento</code> — el lead aparecerá en <em>Lead Performance</em> y en <em>Form Performance</em> al instante.</li>
          </ol>
        </div>
      )}

        </div>
      </details>

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn primary flex items-center gap-2" onClick={onTest} disabled={testing || !data.configured} data-testid="zap-test-btn">
          {testing ? <span className="spinner"/> : <Send size={14}/>}
          {testing ? "Enviando..." : "Probar webhook"}
        </button>
        {testResult && (
          <span className={`pill flex items-center gap-2 ${testResult.ok ? "good" : "bad"}`}>
            {testResult.ok ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>}
            {testResult.msg}
          </span>
        )}
      </div>
    </section>
  );
}

function PayloadExamples({ payloads, singlePayload }) {
  const fallback = singlePayload ? { wpforms: singlePayload } : {};
  const all = payloads || fallback;
  const tabs = [
    { id: "wpforms", label: "WPForms (Orgánico / Google)", color: "#f4c400", icon: "📝",
      desc: "Formulario WordPress con campos ocultos UTM. Para tráfico orgánico, SEO o landings de Google Ads que apuntan a tu sitio." },
    { id: "google_lead_form", label: "Google Lead Form Ext.", color: "#4285f4", icon: "🟦",
      desc: "Google Ads → Lead Form Extension. Usa utm_medium=lead_form para distinguirlo del tráfico web." },
    { id: "meta_lead_ads", label: "Meta Lead Ads", color: "#1877f2", icon: "📘",
      desc: "Formulario nativo de Facebook / Instagram (Meta Lead Ads). Trae IDs específicos de Meta (lead_id, form_id, ad_id, campaign_id)." },
  ];
  const [active, setActive] = useState(tabs[0].id);
  const [copied, setCopied] = useState(false);
  const current = all[active] || {};
  const json = JSON.stringify(current, null, 2);

  const copy = () => {
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <details className="surface p-4 mb-4" style={{ background: "#fafbfc" }} data-testid="payload-examples">
      <summary className="cursor-pointer flex items-center gap-2 font-display font-extrabold">
        <Code size={16}/> Payloads de ejemplo · guía de mapeo (WPForms · Google · Meta)
      </summary>
      <div className="mt-4">
      <p className="text-xs text-gray-600 font-semibold mb-3">
        Cada formulario envía un payload distinto. El campo <code className="bg-gray-100 px-1 rounded">source_type</code> es <strong>opcional pero recomendado</strong> — si no lo envías, el sistema lo infiere de los campos presentes.
        <br/>
        <span style={{ color: "#111827" }}>📌 <strong>business_unit</strong></span>: valores válidos <code className="bg-gray-100 px-1 rounded">Retail</code> o <code className="bg-gray-100 px-1 rounded">Distribuidor</code>.
      </p>

      <div className="lp2-tabs mb-3" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={active === t.id}
            className={`lp2-tab ${active === t.id ? "is-active" : ""}`}
            onClick={() => setActive(t.id)}
            data-testid={`payload-tab-${t.id}`}
            style={active === t.id ? { borderColor: t.color, color: t.color } : {}}
          >
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      <div className="mb-2 p-3 rounded-lg" style={{ background: "#fff", border: `1px solid ${tabs.find(t => t.id === active)?.color}33` }}>
        <p className="text-xs font-semibold text-gray-700">{tabs.find(t => t.id === active)?.desc}</p>
      </div>

      <div className="relative">
        <button
          className="btn flex items-center gap-2 absolute right-2 top-2"
          style={{ fontSize: 11, padding: "4px 10px", zIndex: 2 }}
          onClick={copy}
          data-testid={`payload-copy-${active}`}
        >
          {copied ? <><CheckCircle2 size={12}/> Copiado</> : <><Copy size={12}/> Copiar JSON</>}
        </button>
        <pre className="text-xs p-3 pr-24 rounded-lg overflow-x-auto" style={{ background: "#0f1218", color: "#e5e7eb", maxHeight: 360 }} data-testid={`payload-json-${active}`}>{json}</pre>
      </div>

      <div className="text-xs text-gray-500 mt-2">
        {active === "wpforms" && <>📌 Los UTMs (<code>utm_source</code>, <code>utm_medium</code>, etc.) son los <strong>campos ocultos</strong> que tu formulario WP debe enviar — capturan automáticamente la atribución de la campaña desde la URL.</>}
        {active === "google_lead_form" && <>📌 <strong>Tip</strong>: configura <code>utm_medium=lead_form</code> en la URL de redirección/callback del formulario de Google para que el canal se identifique como <strong>Google Lead Form</strong> y no se confunda con tráfico orgánico.</>}
        {active === "meta_lead_ads" && <>📌 Los IDs de Meta (<code>meta_lead_id</code>, <code>meta_form_id</code>, <code>meta_ad_id</code>, etc.) los envía <strong>Zapier automáticamente</strong> cuando usas el trigger &ldquo;New Lead in Facebook Lead Ads&rdquo;. Solo mapéalos en el paso &ldquo;Custom Request&rdquo; como variables.</>}
      </div>
      </div>
    </details>
  );
}


function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button className="btn flex items-center gap-2" onClick={copy} disabled={!value} aria-label="Copiar">
      {copied ? <><CheckCircle2 size={14}/> Copiado</> : <><Copy size={14}/> Copiar</>}
    </button>
  );
}

function PowerBICard({ data, onUploaded }) {
  return (
    <section className="surface p-6" data-testid="card-powerbi">
      <div className="flex items-start gap-4 mb-5">
        <div className="kpi-icon-clean" style={{ width: 48, height: 48, color: "#f4c400", background: "#fff8d6", boxShadow: "inset 0 0 0 1px #f4c4003f" }}>
          <Database size={22}/>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-extrabold">PowerBI / Dynamics · CSV Upload</h2>
            <StatusBadge ok={data.configured} okText="Activo" badText="Pendiente"/>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Carga manual de CSV exportado desde PowerBI. Reemplaza la data del dashboard <strong>CRM Overview</strong>.
          </p>
        </div>
      </div>

      <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
        <Stat label="Leads cargados" value={fmt(data.leads_count)}/>
        <Stat label="Última carga" value={fmtDate(data.last_ingest_at)}/>
      </div>

      {/* Inline CSV upload */}
      <div className="mb-2">
        <div className="label-up mb-2">Subir nuevo CSV</div>
        <CSVUploadCard onUploaded={onUploaded}/>
      </div>
    </section>
  );
}
function StatusBadge({ ok, okText, badText }) {
  return (
    <span className={`pill ${ok ? "good" : "bad"} flex items-center gap-1`} style={{ fontSize: 11 }}>
      {ok ? <CheckCircle2 size={12}/> : <AlertCircle size={12}/>}
      {ok ? okText : badText}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface p-3" style={{ background: "#fafbfc" }}>
      <div className="label-up mb-1">{label}</div>
      <div className="font-display text-base font-extrabold truncate" title={value}>{value || "—"}</div>
    </div>
  );
}

function CopyField({ label, value, masked = false, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <div className="label-up mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ""}
          readOnly
          className="text-input flex-1"
          style={{ fontFamily: "'Outfit', monospace", fontSize: 13 }}
          data-testid={testid}
        />
        <button className="btn flex items-center gap-2" onClick={copy} disabled={!value} aria-label="Copiar">
          {copied ? <><CheckCircle2 size={14}/> Copiado</> : <><Copy size={14}/> Copiar</>}
        </button>
      </div>
      {masked && <div className="text-xs text-gray-400 mt-1">Token completo visible solo para administradores.</div>}
    </div>
  );
}


/**
 * Maintenance card · one-off admin operations that must survive across
 * deploys. Currently: BDC Response Time backfill trigger. Idempotent.
 */
function MaintenanceCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [polling, setPolling] = useState(false);

  // Poll the backend status every 3s while a backfill is in progress. Cheap
  // — a single doc lookup — and stops automatically when finished.
  useEffect(() => {
    if (!polling) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/admin/backfill-response-tracking/status");
        if (!r.data.running) {
          setPolling(false);
          setRunning(false);
          setResult(r.data.last_result || { ok: true });
        }
      } catch (e) {
        setPolling(false);
        setRunning(false);
        setResult({ error: e.message });
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [polling]);

  const runBackfill = async () => {
    if (!window.confirm(
      "Ejecutar backfill de First Response Time?\n\nProcesará todos los leads históricos y poblará el campo inmutable first_response_hours. Toma ~10 segundos y corre en background — el status se actualizará automáticamente. Idempotente (safe para correr múltiples veces)."
    )) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/backfill-response-tracking");
      if (r.data.status === "started" || r.data.status === "already_running") {
        setPolling(true);
      } else {
        setResult(r.data);
        setRunning(false);
      }
    } catch (e) {
      setResult({ error: e.message || "unknown_error" });
      setRunning(false);
    }
  };

  return (
    <section className="surface p-5 mb-5" data-testid="maintenance-card"
      style={{ background: "#fefce8", border: "1px solid #fde047" }}>
      <div className="flex items-center gap-3 mb-3">
        <Clock size={18} style={{ color: "#854d0e" }}/>
        <h3 className="font-display font-extrabold text-sm"
          style={{ color: "#854d0e", letterSpacing: "-0.01em" }}>
          Mantenimiento · First Response Time Backfill
        </h3>
      </div>
      <p className="text-xs text-gray-700 mb-3" style={{ lineHeight: 1.5 }}>
        Popula el campo inmutable <code>first_response_hours</code> en los leads
        históricos usando la data actual como aproximación. <strong>Correr una sola vez por ambiente</strong>{" "}
        (preview + producción por separado). Idempotente — si ya se corrió, no hace nada.
        {" "}Corre en background (~10s), no cierres la pestaña mientras se ejecuta.
      </p>
      <button onClick={runBackfill} disabled={running}
        className="btn flex items-center gap-2"
        style={{ background: "#f4c400", color: "#111827", border: "1px solid #eab308",
                 opacity: running ? 0.6 : 1 }}
        data-testid="run-backfill-btn">
        {running ? "Procesando en background..." : "▶ Ejecutar backfill"}
      </button>
      {result && !result.error && (
        <div className="mt-3 p-3 rounded-md" style={{ background: "#dcfce7", border: "1px solid #86efac" }}>
          <div className="text-xs font-bold" style={{ color: "#166534" }}>
            ✅ Completado · {result.backfilled ?? 0} leads actualizados
            {(result.backfilled ?? 0) === 0 && " (ya estaba backfilled — no fue necesario)"}
          </div>
        </div>
      )}
      {result?.error && (
        <div className="mt-3 p-3 rounded-md" style={{ background: "#fee2e2", border: "1px solid #fca5a5" }}>
          <div className="text-xs font-bold text-red-700">❌ Error: {result.error}</div>
        </div>
      )}
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────
// Migrate Business Units — one-shot rewriter that re-normalizes
// existing sales.showroom, leads_raw.branch and sales_targets.branch
// against the current business_unit_normalizer alias map. Idempotent.
// Use it after adding new aliases (e.g. Call Center → Contact Center).
// ─────────────────────────────────────────────────────────────────
function MigrateBusinessUnitsCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Re-normalizar Unidades de Negocio?\n\nActualiza los campos branch/showroom en sales, leads_raw y sales_targets usando los aliases actuales (ej. Call Center → Contact Center). Idempotente — safe correr múltiples veces. Solo migra los que difieren del canonical."
    )) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/sales-performance/migrate-business-units");
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message || "unknown_error" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="surface p-5 mb-5" data-testid="migrate-bu-card"
      style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
      <div className="flex items-center gap-3 mb-3">
        <Clock size={18} style={{ color: "#1e40af" }}/>
        <h3 className="font-display font-extrabold text-sm"
          style={{ color: "#1e40af", letterSpacing: "-0.01em" }}>
          Mantenimiento · Re-normalizar Unidades de Negocio
        </h3>
      </div>
      <p className="text-xs text-gray-700 mb-3" style={{ lineHeight: 1.5 }}>
        Aplica el mapa de aliases actual a los campos <code>branch</code> y{" "}
        <code>showroom</code> ya guardados en <code>leads_raw</code>,{" "}
        <code>sales</code> y <code>sales_targets</code>. Úsalo después de
        agregar nuevos aliases (ej. cuando se añadió <code>Call Center → Contact Center</code>).
        Idempotente — si nada cambió, no hace nada. Corre en foreground (~5s).
      </p>
      <button onClick={run} disabled={running}
        className="btn flex items-center gap-2"
        style={{ background: "#3b82f6", color: "#fff", border: "1px solid #2563eb",
                 opacity: running ? 0.6 : 1 }}
        data-testid="run-migrate-bu-btn">
        {running ? "Ejecutando..." : "▶ Re-normalizar Unidades de Negocio"}
      </button>
      {result && !result.error && (
        <div className="mt-3 p-3 rounded-md" style={{ background: "#dcfce7", border: "1px solid #86efac" }}>
          <div className="text-xs font-bold mb-1" style={{ color: "#166534" }}>
            ✅ Completado
          </div>
          <ul className="text-xs" style={{ color: "#166534" }}>
            {Object.entries(result).map(([coll, stats]) => (
              <li key={coll}>
                <strong>{coll}</strong>: {stats?.docs_updated ?? 0} docs actualizados
                {" · "}{stats?.aliases_normalized ?? 0} aliases aplicados
              </li>
            ))}
          </ul>
        </div>
      )}
      {result?.error && (
        <div className="mt-3 p-3 rounded-md" style={{ background: "#fee2e2", border: "1px solid #fca5a5" }}>
          <div className="text-xs font-bold text-red-700">❌ Error: {String(result.error)}</div>
        </div>
      )}
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────
// Master Template — download the empty XLSX skeleton (4 sheets) so
// data/IT team can hand it to the Dynamics/SAP export owners as the
// target schema for the daily consolidated master file.
// ─────────────────────────────────────────────────────────────────
function MasterTemplateCard() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await api.get("/sales-performance/download/master-template", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sdm_master_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error al descargar");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-2xl border p-5 bg-white" data-testid="master-template-card"
         style={{ borderColor: "#e2e8f0" }}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl grid place-items-center flex-shrink-0"
               style={{ width: 40, height: 40,
                        background: "#f4c40015", color: "#a78100",
                        boxShadow: "inset 0 0 0 1px #f4c40033" }}>
            <FileSpreadsheet size={20} strokeWidth={2.2}/>
          </div>
          <div>
            <div className="font-display font-extrabold text-base leading-tight"
                 style={{ color: "#111827" }}>
              Template Maestro CRM/Ventas
            </div>
            <div className="text-xs text-gray-500 font-semibold mt-1 max-w-xl">
              XLSX vacío con las 4 hojas requeridas (Leads · Oportunidades · Cotizaciones · Facturacion)
              y sus columnas exactas. Entregar al equipo de data/IT como referencia del formato del
              archivo maestro diario.
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="btn primary flex items-center gap-2"
          data-testid="master-template-download-btn">
          <Download size={16} strokeWidth={2.4}/>
          {downloading ? "Descargando…" : "Descargar template"}
        </button>
        <span className="text-[11px] text-gray-500 font-semibold">
          47 columnas · 4 hojas · Hoja <code>_INSTRUCCIONES</code> con las reglas
        </span>
      </div>

      {error && (
        <div className="mt-3 text-xs font-semibold text-red-700 bg-red-50 px-3 py-2 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// CRM XLSX Upload — reusable card for Oportunidades + Cotizaciones.
// Both endpoints share the same contract (multipart file + optional
// replace_year / replace_month) so a single component covers both.
// ─────────────────────────────────────────────────────────────────
function CrmXlsxUploadCard({ title, subtitle, endpoint, statusKey, testid }) {
  const [file, setFile] = useState(null);
  const [replaceMode, setReplaceMode] = useState(false);
  const [replaceYear, setReplaceYear] = useState("");
  const [replaceMonth, setReplaceMonth] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    let alive = true;
    api.get("/sales-performance/uploads/status")
      .then((r) => { if (alive) setStatus(r.data?.[statusKey] || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [statusKey, result]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (replaceMode && replaceYear && replaceMonth) {
        fd.append("replace_year", replaceYear);
        fd.append("replace_month", replaceMonth);
      }
      const r = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(r.data);
      setFile(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="surface p-5 mb-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h4 className="font-display font-extrabold text-base leading-tight">{title}</h4>
          <p className="text-xs text-gray-500 font-semibold mt-0.5">{subtitle}</p>
        </div>
        {status && status.total > 0 && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">Cargados</div>
            <div className="font-display font-extrabold text-sm" style={{ fontVariantNumeric: "tabular-nums" }}>
              {status.total.toLocaleString("es-DO")}
            </div>
            {status.latest_month && (
              <div className="text-[10px] font-bold text-gray-500">
                Último: {status.latest_month.month} {status.latest_month.year}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={`${testid}-file`}
          className="text-sm"
        />

        <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer select-none">
          <input
            type="checkbox"
            checked={replaceMode}
            onChange={(e) => setReplaceMode(e.target.checked)}
            className="sdm-checkbox"
            style={{ width: 14, height: 14 }}
            data-testid={`${testid}-replace-toggle`}
          />
          Reemplazar mes específico (por defecto: append por GUID)
        </label>

        {replaceMode && (
          <div className="flex gap-2">
            <select value={replaceYear} onChange={(e) => setReplaceYear(e.target.value)}
                    className="text-input text-xs" style={{ maxWidth: 120 }}
                    data-testid={`${testid}-year`}>
              <option value="">Año…</option>
              {[currentYear, currentYear - 1, currentYear - 2].map((y) =>
                <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={replaceMonth} onChange={(e) => setReplaceMonth(e.target.value)}
                    className="text-input text-xs" style={{ maxWidth: 120 }}
                    data-testid={`${testid}-month`}>
              <option value="">Mes…</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}

        <button
          onClick={upload}
          disabled={!file || uploading || (replaceMode && (!replaceYear || !replaceMonth))}
          className="btn primary"
          data-testid={`${testid}-submit`}
          style={{ opacity: (!file || uploading) ? 0.5 : 1 }}
        >
          {uploading ? "Subiendo…" : "Subir archivo"}
        </button>

        {result && (
          <div className="p-2 rounded text-xs font-bold"
               style={{ background: "#dcfce7", color: "#166534" }}
               data-testid={`${testid}-success`}>
            ✓ {result.total_rows?.toLocaleString("es-DO")} filas · {result.inserted?.toLocaleString("es-DO")} nuevas · {result.updated?.toLocaleString("es-DO")} actualizadas
            {result.with_test_drive != null && <> · {result.with_test_drive.toLocaleString("es-DO")} con Test Drive</>}
          </div>
        )}
        {error && (
          <div className="p-2 rounded text-xs font-bold"
               style={{ background: "#fee2e2", color: "#991b1b" }}
               data-testid={`${testid}-error`}>
            ✗ {error}
          </div>
        )}
      </div>
    </div>
  );
}
